import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

const services = [
  {
    number: "01",
    title: "Product Sourcing",
    description:
      "Access a vast network of verified suppliers across industrial and construction categories. We handle vetting, negotiation, and procurement.",
  },
  {
    number: "02",
    title: "Trading & Distribution",
    description:
      "Seamless B2B and B2C trading solutions with logistics support. Competitive pricing for bulk and retail orders.",
  },
  {
    number: "03",
    title: "E-Commerce Solutions",
    description:
      "Ready-to-sell products for your online store. We support Amazon, Flipkart, and independent e-commerce platforms.",
  },
  {
    number: "04",
    title: "Partnership Programs",
    description:
      "Long-term partnerships with exclusive pricing, priority support, and dedicated account management.",
  },
  {
    number: "05",
    title: "Market Research",
    description:
      "Data-driven insights on trending products, pricing strategies, and market opportunities to guide your decisions.",
  },
  {
    number: "06",
    title: "Quality Assurance",
    description:
      "Rigorous inspection and testing protocols to ensure every product meets international standards before shipment.",
  },
];

export function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="services" className="py-32 bg-background relative">
      {/* Subtle accents */}
      <div className="absolute top-0 left-0 w-full h-px bg-border" />
      
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-20"
        >
          <span className="inline-block text-xs tracking-wide-elegant uppercase text-muted-foreground mb-6">
            What We Offer
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-8 leading-[1.1] font-medium">
            Trading & E-Commerce
            <br />
            <span className="text-muted-foreground">Services</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            From product sourcing to e-commerce fulfilment, SAILKHA TRADE offers end-to-end 
            trading services designed to simplify your supply chain and grow your business.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
              className="group bg-background p-8 lg:p-10 hover:bg-muted/30 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="text-4xl font-display text-muted-foreground/30 group-hover:text-foreground transition-colors duration-300">
                  {service.number}
                </span>
                <ArrowUpRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 -translate-y-1 translate-x-1 group-hover:translate-y-0 group-hover:translate-x-0 transition-all duration-300" />
              </div>
              <h3 className="font-display text-2xl text-foreground mb-4">
                {service.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
